import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, ModalVariant, Spinner } from '@patternfly/react-core';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { configurationTargetSelectors } from '../../store/configurationTarget/configurationTargetSlice';
import { getUserName } from '../../store/configurationTarget/mvision-client-list';
import { BackupInfo } from '../../store/backup/backup-types';
import { isValidTimestamp, unixTimestampToIsoDateTimeWithRelative } from '../../util/date-convert';
import { backupListFetchRequested, backupRestoreErrorCleared, backupRestoreRequested } from '../../store/backup/backupSlice';
import { StoreState } from '../../store/store';
import { ModelType } from '../../store/global-types/customization-types';
import { contouringSelectors } from '../../store/contouring/contouringSlice';
import { doseSelectors } from '../../store/dose/doseSlice';
import { imageSelectors } from '../../store/image/imageSlice';
import { adaptSelectors } from '../../store/adapt/adaptSlice';
import './UserSettings.css';

interface ConfigurationBackupRestoreProps {
    modelTypeUiString: string;
    modelType: ModelType;
}

const ConfigurationBackupRestore = (props: ConfigurationBackupRestoreProps) => {
    const { modelTypeUiString, modelType } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const backupRestoreInProgress = useSelector((state: StoreState) =>
        state.backup.restoreModelType === modelType && state.backup.backupRestoreInProgress);
    const anyBackupRestoreInProgress = useSelector((state: StoreState) =>
        state.backup.backupRestoreInProgress);
    const backupRestoreError = useSelector((state: StoreState) =>
        state.backup.restoreModelType === modelType ? state.backup.backupRestoreError : null);
    const backups = useSelector((state: StoreState) =>
        state.backup.listModelType === modelType ? state.backup.backupList : []);
    const isLoadingBackups = useSelector((state: StoreState) =>
        state.backup.listModelType === modelType && state.backup.backupListLoading);
    const loadError = useSelector((state: StoreState) =>
        state.backup.listModelType === modelType ? state.backup.backupListError : null);

    const anyUnsavedContourChanges = useSelector(contouringSelectors.selectAnyUnsavedContouringChangesInApp);
    const anyUnsavedDoseChanges = useSelector(doseSelectors.selectAnyUnsavedDoseChangesInApp);
    const anyUnsavedImageChanges = useSelector(imageSelectors.selectAnyUnsavedImageChangesInApp);
    const anyUnsavedAdaptChanges = useSelector(adaptSelectors.selectAnyUnsavedAdaptChangesInApp);

    const hasUnsavedChanges = useMemo(() => {
        switch (modelType) {
            case ModelType.Contouring: return anyUnsavedContourChanges;
            case ModelType.Dose: return anyUnsavedDoseChanges;
            case ModelType.Image: return anyUnsavedImageChanges;
            case ModelType.Adapt: return anyUnsavedAdaptChanges;
            default: throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [modelType, anyUnsavedContourChanges, anyUnsavedDoseChanges, anyUnsavedImageChanges, anyUnsavedAdaptChanges]);

    const [isListModalOpen, setIsListModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedBackup, setSelectedBackup] = useState<BackupInfo | null>(null);
    const [restoringFilename, setRestoringFilename] = useState<string | null>(null);
    const [restoreSuccess, setRestoreSuccess] = useState(false);

    // Track restore completion
    const [wasRestoring, setWasRestoring] = useState(false);

    useEffect(() => {
        if (wasRestoring && !backupRestoreInProgress) {
            if (!backupRestoreError) {
                setRestoreSuccess(true);
                setIsListModalOpen(false);
            }
            setWasRestoring(false);
            setRestoringFilename(null);
        }
    }, [backupRestoreInProgress, backupRestoreError, wasRestoring]);

    const handleOpenBackupList = useCallback(() => {
        if (!currentTarget) { return; }

        setRestoreSuccess(false);
        // Clear any stale error from a previous restore attempt
        dispatch(backupRestoreErrorCleared());
        setIsListModalOpen(true);
        dispatch(backupListFetchRequested({ appClient: currentTarget, modelType }));
    }, [currentTarget, dispatch, modelType]);

    const handleCloseListModal = useCallback(() => {
        setIsListModalOpen(false);
        setSelectedBackup(null);
    }, []);

    const handleSelectBackup = useCallback((backup: BackupInfo) => {
        setSelectedBackup(backup);
        setIsConfirmModalOpen(true);
    }, []);

    const handleCloseConfirmModal = useCallback(() => {
        setIsConfirmModalOpen(false);
        setSelectedBackup(null);
    }, []);

    const handleConfirmRestore = useCallback(() => {
        if (!currentTarget || !selectedBackup) { return; }

        dispatch(backupRestoreErrorCleared());
        dispatch(backupRestoreRequested({ appClient: currentTarget, backupFilename: selectedBackup.filename, modelType }));
        setRestoringFilename(selectedBackup.filename);
        setWasRestoring(true);
        setIsConfirmModalOpen(false);
        setSelectedBackup(null);
    }, [currentTarget, selectedBackup, dispatch, modelType]);

    const formatBackupTime = useCallback((backup: BackupInfo): string => {
        return isValidTimestamp(backup.t_modified_u)
            ? unixTimestampToIsoDateTimeWithRelative(backup.t_modified_u)
            : backup.time_modified;
    }, []);

    if (!currentTarget) {
        return null;
    }

    return (
        <>
            <div className="setting-item">
                <span className="label">{t('settings.backupRestore')}</span>
                <span className="value">
                    <Button
                        variant="secondary"
                        onClick={handleOpenBackupList}
                        isDisabled={anyBackupRestoreInProgress}
                        isLoading={backupRestoreInProgress}
                    >
                        {t('settings.backupRestore.button')}
                    </Button>
                </span>
            </div>

            {restoreSuccess && (
                <div className="configuration-reset-completed-box">
                    <Alert variant="info" isInline title={t('settings.backupRestore.success')} />
                </div>
            )}

            <Modal
                variant={ModalVariant.large}
                title={t('settings.backupRestore.list.title')}
                isOpen={isListModalOpen}
                showClose
                onClose={handleCloseListModal}
                actions={[
                    <Button key="close" variant="tertiary" onClick={handleCloseListModal}>{t('common.close')}</Button>
                ]}
            >
                {isLoadingBackups && (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <Spinner size="lg" />
                    </div>
                )}

                {loadError && (
                    <Alert variant="danger" isInline title={t('settings.backupRestore.loadError')}>
                        {loadError}
                    </Alert>
                )}

                {backupRestoreError && (
                    <Alert variant="danger" isInline title={t('settings.backupRestore.restoreError')} style={{ marginBottom: '1rem' }}>
                        {backupRestoreError}
                    </Alert>
                )}

                {hasUnsavedChanges && (
                    <Alert variant="warning" isInline title={t('settings.backupRestore.unsavedChanges')} style={{ marginBottom: '1rem' }} />
                )}

                {!isLoadingBackups && !loadError && backups.length === 0 && (
                    <Alert variant="info" isInline title={t('settings.backupRestore.noBackups')} />
                )}

                {!isLoadingBackups && !loadError && backups.length > 0 && (
                    <table className="pf-v5-c-table pf-m-grid-md backup-list-table" role="grid">
                        <thead>
                            <tr>
                                <th>{t('settings.backupRestore.column.date')}</th>
                                <th>{t('settings.backupRestore.column.operation')}</th>
                                <th>{t('settings.backupRestore.column.user')}</th>
                                <th>{t('settings.backupRestore.column.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backups.map((backup) => (
                                <tr key={backup.filename}>
                                    <td>{formatBackupTime(backup)}</td>
                                    <td>{backup.operation}</td>
                                    <td>{backup.user_name || backup.modified_by}</td>
                                    <td>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleSelectBackup(backup)}
                                            isDisabled={anyBackupRestoreInProgress || hasUnsavedChanges}
                                            isLoading={restoringFilename === backup.filename && backupRestoreInProgress}
                                        >
                                            {t('settings.backupRestore.restore')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal>

            <Modal
                variant={ModalVariant.small}
                title={t('settings.backupRestore.confirm.title')}
                isOpen={isConfirmModalOpen && selectedBackup !== null}
                showClose
                onClose={handleCloseConfirmModal}
                actions={[
                    <Button key="confirm" variant="primary" onClick={handleConfirmRestore}>{t('settings.backupRestore.confirm.button')}</Button>,
                    <Button key="cancel" variant="tertiary" onClick={handleCloseConfirmModal}>{t('common.cancel')}</Button>
                ]}
            >
                <div>
                    <Trans i18nKey="settings.backupRestore.confirm.message">
                        Restore <span className="configuration-target-model-type">{{ modelType: modelTypeUiString }}</span> configuration for <span className="reset-emphasis configuration-target-name">{{ configurationTargetName: getUserName(currentTarget) }}</span> from backup dated <strong>{{ backupDate: selectedBackup ? formatBackupTime(selectedBackup) : '' }}</strong>?
                    </Trans>
                </div>
                <div>
                    {t('settings.backupRestore.confirm.warning')}
                </div>
            </Modal>
        </>
    );
};

export default ConfigurationBackupRestore;
